import * as Sentry from '@sentry/nextjs';
import type { NextApiRequest, NextApiResponse } from 'next';
import { runMiddleware, validateJwt } from '../../middleware';
import { supabase } from '../../supabase';
import { Database } from '../../../../types';

export enum LoyaltyChallengeStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  DENIED = 'denied',
}

export enum LoyaltyChallengeType {
  EMAIL = 'email',
  PHOTO = 'photo',
  TEXT = 'text',
  REFERRAL = 'referral',
}

export type LoyaltyChallengeSubmissionData = { submission: string } | { imageUrl: string } | null;

export type LoyaltyChallengeSubmissionMin = { data: LoyaltyChallengeSubmissionData, status: LoyaltyChallengeStatus, id: string, points: number };

export type LoyaltyChallenge = Omit<Database['public']['Tables']['loyalty_challenges']['Row'], 'data' | 'type' | 'secret'>
  & (
    {
      type: LoyaltyChallengeType.PHOTO;
      data: { links?: { url: string, text: string }[] } | null;
    }
    | {
      type: LoyaltyChallengeType.TEXT;
      data: { links?: { url: string, text: string }[] } | null;
    }
    | {
      type: LoyaltyChallengeType.EMAIL;
      data: null;
    }
    | {
      type: LoyaltyChallengeType.REFERRAL;
      data: null;
    }
  )
  & {
    submissions: LoyaltyChallengeSubmissionMin[]
    // hide `secret`, flag if it exists
    secret: boolean
  };

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse<{ challenges: LoyaltyChallenge[] }>,
) {
  await runMiddleware(req, res, validateJwt);
  const address = req.body.jwt.address;
  const { artist, referrer: maybeReferrer } = req.query;

  const { data, error } = await supabase
    .from('loyalty_challenges')
    .select(`
      *,
      submissions:loyalty_challenge_users(data,status,points,id)
    `)
    .eq('artist', artist)
    .eq("loyalty_challenge_users.user_address", address)
    .order('points');

  if (error) {
    console.error(`error fetching loyalty challenges for artist ${artist} user ${address}`, error)
    res.status(500).end();
    return;
  }

  const user = await supabase
    .from('loyalty_artist_users')
    .select('*')
    .eq('artist', artist)
    .eq('user_address', address)
    .maybeSingle();

  if (user.error) {
    console.error(`error fetching loyalty user for artist ${artist} user ${address}`, user.error)
    res.status(500).end();
    return;
  }

  await Promise.all([
    // complete refer challenge if user is new to the artist
    !user.data
      && maybeReferrer
      ? completeReferChallenge(maybeReferrer as string, address, artist as string)
      : Promise.resolve(),
    // insert new user or update last_seen_at
    supabase
      .from('loyalty_artist_users')
      .upsert({
        artist: artist as string,
        user_address: address,
        last_seen_at: new Date().toISOString()
      })
  ]);

  const challenges = await Promise.all(data
    .map(async c => {
      return {
        ...c,
        submissions: await Promise.all((c.submissions as LoyaltyChallengeSubmissionMin[]).map(loyaltySubmissionWithSignedImageUrl)),
        secret: !!c.secret
      } as LoyaltyChallenge
    }));

  res.status(200).json({ challenges });
}

export async function loyaltySubmissionWithSignedImageUrl(submission: LoyaltyChallengeSubmissionMin) {
  if (!submission.data || !('imageUrl' in submission.data)) {
    return submission;
  }
  submission.data.imageUrl = (await supabase.storage.from('loyalty-submissions').createSignedUrl(submission.data?.imageUrl, 60 * 60)).data?.signedUrl!;
  return submission;
}

async function completeReferChallenge(referrer: string, referred_user: string, artist: string) {
  const { data, error } = await supabase
    .from('loyalty_challenges')
    .select(`
      id,
      limit,
      points,
      submissions:loyalty_challenge_users(user_address,status)
    `)
    .eq('type', 'referral')
    .eq('artist', artist)
    .eq("loyalty_challenge_users.user_address", referrer)
    .maybeSingle();

  if (error) {
    Sentry.captureMessage(`Error fetching refer challenge for referrer: ${referrer} referred_user: ${referred_user} artist: ${artist}`);
    return;
  }

  if (!data || data.submissions.length >= data.limit) {
    // either no challenge configured or already completed
    return;
  }

  const { error: insertError } = await supabase
    .from('loyalty_challenge_users')
    .insert({
      challenge_id: data.id,
      user_address: referrer,
      data: { referred_user },
      status: LoyaltyChallengeStatus.COMPLETED,
      points: data.points
    });

  if (insertError) {
    Sentry.captureMessage(`Error completing refer challenge for referrer: ${referrer} referred_user: ${referred_user} artist: ${artist}`);
  }
}